(() => {
  const SELECTOR_ROOT = '.buy-buttons-block[data-purchase-mode]';
  const SELECTOR_TOGGLE = '.wb-purchase-mode';
  const SELECTOR_MOUNT = '[data-anygift="ProductPageGiftUi"]';
  const SELECTOR_CTA = `${SELECTOR_MOUNT} .anygift-e-gift-modal__open-button`;
  const SELECTOR_PLACEHOLDER = '.wb-egift-cart-placeholder';
  const observedRoots = new WeakSet();
  const pendingCtaClicks = new WeakMap();

  function hasAnyGiftCta(root) {
    if (!root) return false;
    return Boolean(
      root.querySelector(SELECTOR_CTA) || root.querySelector('.anygift-e-gift-modal__open-button')
    );
  }

  function syncPlaceholder(root) {
    const placeholder = root && root.querySelector(SELECTOR_PLACEHOLDER);
    if (!placeholder) return;
    const visible =
      root.getAttribute('data-purchase-mode') === 'egift' &&
      root.getAttribute('data-anygift-ready') !== 'true';
    placeholder.setAttribute('aria-hidden', visible ? 'false' : 'true');
    placeholder.tabIndex = visible ? 0 : -1;
  }

  function flushPendingCtaClick(root) {
    if (!pendingCtaClicks.get(root)) return;
    const cta = root.querySelector(SELECTOR_CTA);
    if (!cta) return;
    pendingCtaClicks.delete(root);
    cta.click();
  }

  function revealUi() {
    document.documentElement.classList.add('wb-egift-ui');
  }

  function optionContainers(root) {
    const details = (root && root.closest('.product-details')) || document;
    return details.querySelectorAll('#infiniteoptions-container, .wb-infinite-options-container');
  }

  function resetOptionFields(container) {
    container.querySelectorAll('input, select, textarea').forEach((el) => {
      if (el.type === 'checkbox' || el.type === 'radio') {
        if (!el.checked) return;
        el.checked = false;
        notifyOptionField(el);
        return;
      }

      if (el.tagName === 'SELECT') {
        if (el.selectedIndex === 0) return;
        el.selectedIndex = 0;
        notifyOptionField(el);
        return;
      }

      if (!el.value) return;
      el.value = '';
      notifyOptionField(el);
    });
  }

  function notifyOptionField(el) {
    el.dispatchEvent(new Event('input', { bubbles: true }));
    el.dispatchEvent(new Event('change', { bubbles: true }));
  }

  function setOptionFieldsEnabled(container, enabled) {
    container.querySelectorAll('input, select, textarea').forEach((el) => {
      el.disabled = !enabled;
    });
  }

  function syncOptionFields(root, mode) {
    optionContainers(root).forEach((container) => {
      setOptionFieldsEnabled(container, true);
      resetOptionFields(container);
      if (mode === 'egift') setOptionFieldsEnabled(container, false);
    });
  }

  function syncCtaType(root) {
    root.querySelectorAll(SELECTOR_CTA).forEach((cta) => {
      if (cta.tagName === 'BUTTON') cta.setAttribute('type', 'button');
    });
  }

  function syncReady(root) {
    if (!root) return;
    if (hasAnyGiftCta(root)) {
      root.setAttribute('data-anygift-ready', 'true');
      revealUi();
      flushPendingCtaClick(root);
    }
    syncCtaType(root);
    syncMountA11y(root);
    syncPlaceholder(root);
    syncDetails(root);
  }

  function syncMountA11y(root) {
    const mount = root && root.querySelector(SELECTOR_MOUNT);
    if (!mount) return;
    const visible =
      root.getAttribute('data-purchase-mode') === 'egift' &&
      root.getAttribute('data-anygift-ready') === 'true';
    mount.setAttribute('aria-hidden', visible ? 'false' : 'true');
  }

  function observeRoot(root) {
    if (!root || observedRoots.has(root)) return;
    observedRoots.add(root);
    const observer = new MutationObserver(() => syncReady(root));
    observer.observe(root, { childList: true, subtree: true });
  }

  function applyMode(root, mode) {
    if (!root || (mode !== 'normal' && mode !== 'egift')) return;

    root.setAttribute('data-purchase-mode', mode);

    const details = root.closest('.product-details');
    if (details) details.setAttribute('data-purchase-mode', mode);

    root.querySelectorAll(`${SELECTOR_TOGGLE} [data-mode]`).forEach((btn) => {
      const active = btn.getAttribute('data-mode') === mode;
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-checked', active ? 'true' : 'false');
    });

    syncOptionFields(root, mode);
    syncCtaType(root);
    syncMountA11y(root);
    syncPlaceholder(root);
  }

  function modeButtons(root) {
    return Array.from(root.querySelectorAll(`${SELECTOR_TOGGLE} [data-mode]`));
  }

  function syncDetails(root) {
    const mode = root.getAttribute('data-purchase-mode');
    const details = root.closest('.product-details');
    if (details && mode) details.setAttribute('data-purchase-mode', mode);
  }

  function syncAll() {
    document.querySelectorAll(SELECTOR_ROOT).forEach((root) => {
      observeRoot(root);
      syncReady(root);
    });
  }

  function start() {
    syncAll();
    const poll = window.setInterval(syncAll, 50);
    window.setTimeout(() => window.clearInterval(poll), 20000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  document.addEventListener('shopify:section:load', syncAll);

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      const root = form.querySelector(SELECTOR_ROOT) || form.closest(SELECTOR_ROOT);
      if (!root || root.getAttribute('data-purchase-mode') !== 'egift') return;
      event.preventDefault();
      event.stopPropagation();
    },
    true
  );

  document.addEventListener('click', (event) => {
    const toggleBtn = event.target.closest(`${SELECTOR_TOGGLE} [data-mode]`);
    if (toggleBtn) {
      const root = toggleBtn.closest(SELECTOR_ROOT);
      if (!root) return;
      applyMode(root, toggleBtn.getAttribute('data-mode'));
      return;
    }

    const placeholder = event.target.closest(SELECTOR_PLACEHOLDER);
    if (!placeholder) return;
    const root = placeholder.closest(SELECTOR_ROOT);
    if (!root || root.getAttribute('data-purchase-mode') !== 'egift') return;
    event.preventDefault();
    const cta = root.querySelector(SELECTOR_CTA);
    if (cta) {
      cta.click();
      return;
    }
    pendingCtaClicks.set(root, true);
  });

  document.addEventListener('keydown', (event) => {
    const btn = event.target.closest(`${SELECTOR_TOGGLE} [data-mode]`);
    if (!btn) return;
    const root = btn.closest(SELECTOR_ROOT);
    if (!root) return;

    if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault();
      applyMode(root, btn.getAttribute('data-mode'));
      return;
    }

    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;

    const buttons = modeButtons(root);
    const index = buttons.indexOf(btn);
    if (index < 0) return;

    event.preventDefault();
    const nextIndex =
      event.key === 'ArrowRight'
        ? (index + 1) % buttons.length
        : (index - 1 + buttons.length) % buttons.length;
    buttons[nextIndex].focus();
  });
})();
